import React from "react";
import { Dropdown, Form, Row, Col, Button, ButtonGroup } from "react-bootstrap";

type DateLike = string | Date | null | undefined;

export interface DateRangeValue {
  startDate: string;
  endDate: string;
}

export interface DateRangePreset {
  key: string;
  label: string;
  getRange: () => { start: Date; end: Date };
}

interface DateRangePickerProps {
  startDate?: DateLike;
  endDate?: DateLike;
  onChange: (range: DateRangeValue) => void;
  placeholder?: string;
  locale?: string;
  disabled?: boolean;
  min?: string;
  max?: string;
  size?: "sm" | "lg";
  className?: string;
  menuClassName?: string;
  toggleVariant?: string;
  withPresets?: boolean;
  presets?: DateRangePreset[];
}

const toISODate = (value: DateLike): string => {
  if (!value) return "";
  const d = typeof value === "string" ? new Date(value) : value;
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString().split("T")[0];
};

const formatDateLabel = (value: DateLike, locale: string): string => {
  if (!value) return "";
  const d = typeof value === "string" ? new Date(value) : value;
  if (Number.isNaN(d.getTime())) return "";
  // dd.MM.yyyy for TR by default using Intl ensures correct punctuation
  return new Intl.DateTimeFormat(locale || "tr-TR", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(d);
};

const defaultPresets: DateRangePreset[] = [
  {
    key: "today",
    label: "Today",
    getRange: () => {
      const end = new Date();
      const start = new Date(end);
      return { start, end };
    },
  },
  {
    key: "7d",
    label: "Last 7 Days",
    getRange: () => {
      const end = new Date();
      const start = new Date();
      start.setDate(end.getDate() - 6);
      return { start, end };
    },
  },
  {
    key: "30d",
    label: "Last 30 Days",
    getRange: () => {
      const end = new Date();
      const start = new Date();
      start.setDate(end.getDate() - 29);
      return { start, end };
    },
  },
  {
    key: "thisMonth",
    label: "This Month",
    getRange: () => {
      const end = new Date();
      const start = new Date(end.getFullYear(), end.getMonth(), 1);
      return { start, end };
    },
  },
  {
    key: "lastMonth",
    label: "Last Month",
    getRange: () => {
      const end = new Date();
      const firstOfThis = new Date(end.getFullYear(), end.getMonth(), 1);
      const start = new Date(firstOfThis);
      start.setMonth(start.getMonth() - 1);
      const lastOfPrev = new Date(firstOfThis);
      lastOfPrev.setDate(0);
      return { start, end: lastOfPrev };
    },
  },
];

export const DateRangePicker: React.FC<DateRangePickerProps> = ({
  startDate,
  endDate,
  onChange,
  placeholder = "Tarih aralığı seçin",
  locale = "tr-TR",
  disabled,
  min,
  max,
  size,
  className,
  menuClassName,
  toggleVariant = "outline-secondary",
  withPresets = true,
  presets,
}) => {
  const [open, setOpen] = React.useState(false);
  const [tempStart, setTempStart] = React.useState<string>(toISODate(startDate || ""));
  const [tempEnd, setTempEnd] = React.useState<string>(toISODate(endDate || ""));

  React.useEffect(() => {
    if (open) {
      setTempStart(toISODate(startDate || ""));
      setTempEnd(toISODate(endDate || ""));
    }
  }, [open]);

  React.useEffect(() => {
    setTempStart(toISODate(startDate || ""));
  }, [startDate]);

  React.useEffect(() => {
    setTempEnd(toISODate(endDate || ""));
  }, [endDate]);

  const selectedLabel =
    tempStart && tempEnd
      ? `${formatDateLabel(tempStart, locale)} — ${formatDateLabel(tempEnd, locale)}`
      : placeholder;

  const computedPresets = presets && presets.length > 0 ? presets : defaultPresets;

  const rangeInvalid = !tempStart || !tempEnd || new Date(tempStart) > new Date(tempEnd);

  const apply = () => {
    if (rangeInvalid) return;
    onChange({ startDate: tempStart, endDate: tempEnd });
    setOpen(false);
  };

  const reset = () => {
    setTempStart("");
    setTempEnd("");
  };

  const applyPreset = (p: DateRangePreset) => {
    const { start, end } = p.getRange();
    const s = toISODate(start);
    const e = toISODate(end);
    setTempStart(s);
    setTempEnd(e);
  };

  return (
    <Dropdown show={open} onToggle={(s) => setOpen(!!s)}>
      <Dropdown.Toggle
        variant={toggleVariant}
        className={className}
        size={size}
        disabled={disabled}
      >
        {selectedLabel}
      </Dropdown.Toggle>
      <Dropdown.Menu className={menuClassName} align="start">
        <div className="d-flex flex-column gap-2">
          {withPresets && (
            <div className="mb-2">
              <ButtonGroup size="sm">
                {computedPresets.map((p) => (
                  <Button key={p.key} variant="outline-secondary" onClick={() => applyPreset(p)}>
                    {p.label}
                  </Button>
                ))}
              </ButtonGroup>
            </div>
          )}
          <Row className="g-2">
            <Col>
              <Form.Label className="small text-muted">Start</Form.Label>
              <Form.Control
                type="date"
                size="sm"
                value={tempStart}
                min={min}
                max={max}
                onChange={(e) => setTempStart(e.target.value)}
              />
            </Col>
            <Col>
              <Form.Label className="small text-muted">End</Form.Label>
              <Form.Control
                type="date"
                size="sm"
                value={tempEnd}
                min={min}
                max={max}
                onChange={(e) => setTempEnd(e.target.value)}
              />
            </Col>
          </Row>
          {rangeInvalid && (
            <div className="mt-1 small text-danger">End date must be after start date.</div>
          )}
          <div className="d-flex justify-content-end gap-2 mt-2">
            <Button variant="outline-secondary" size="sm" onClick={reset}>
              Reset
            </Button>
            <Button variant="primary" size="sm" onClick={apply} disabled={rangeInvalid}>
              Apply
            </Button>
          </div>
        </div>
      </Dropdown.Menu>
    </Dropdown>
  );
};

export default DateRangePicker;


